import { useState, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Upload, FileText, AlertTriangle, CheckCircle2, XCircle,
  ChevronRight, Download, ArrowLeft, Layers, Plus, RefreshCw, Merge,
  SkipForward,
} from "lucide-react";
import { useCatalog, type CatalogProduct, type CatalogVariant } from "@/data/CatalogContext";
import {
  CSV_HEADERS, CSV_HEADER_LABELS,
  type CsvRow, type ParsedImportRow, type ConflictAction, type ImportBatch,
} from "@/data/catalogImportTypes";
import { useRole } from "@/data/RoleContext";

/* ═══════════════════════════════════════════════════════
   CSV Import Wizard
   ═══════════════════════════════════════════════════════ */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  importBatches?: ImportBatch[];
  setImportBatches?: React.Dispatch<React.SetStateAction<ImportBatch[]>>;
}

const STEPS = ["Upload", "Map Columns", "Review Conflicts", "Preview", "Commit"];

export default function CatalogImportWizard({ open, onOpenChange, importBatches: externalImportBatches, setImportBatches: externalSetImportBatches }: Props) {
  const { categories, products, setProducts } = useCatalog();
  const { role } = useRole();
  const [step, setStep] = useState(0);
  const [rawText, setRawText] = useState("");
  const [fileName, setFileName] = useState("");
  const [headerRow, setHeaderRow] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, keyof CsvRow | "">>({}); 
  const [parsedRows, setParsedRows] = useState<ParsedImportRow[]>([]);
  const [internalImportBatches, setInternalImportBatches] = useState<ImportBatch[]>([]);

  const importBatches = externalImportBatches ?? internalImportBatches;
  const setImportBatches = externalSetImportBatches ?? setInternalImportBatches;

  const reset = () => {
    setStep(0); setRawText(""); setFileName(""); setHeaderRow([]); setDataRows([]);
    setColumnMap({}); setParsedRows([]);
    if (!externalSetImportBatches) {
      setInternalImportBatches([]);
    }
  };

  /* ── Step 1: Upload ── */
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setRawText(text);
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headers = parseCSVLine(lines[0]);
      setHeaderRow(headers);
      setDataRows(lines.slice(1).map(parseCSVLine));
      // Auto-map columns
      const autoMap: Record<string, keyof CsvRow | ""> = {};
      headers.forEach((h, i) => {
        const normalized = h.toLowerCase().replace(/[\s_-]/g, "");
        const match = CSV_HEADERS.find(k => k.toLowerCase() === normalized
          || CSV_HEADER_LABELS[k].toLowerCase().replace(/[\s_-]/g, "") === normalized);
        autoMap[String(i)] = match || "";
      });
      setColumnMap(autoMap);
      setStep(1);
    };
    reader.readAsText(file);
  };

  /* ── Step 2: Validate & Map ── */
  const requiredFields: (keyof CsvRow)[] = ["category", "productName"];
  const mappedFields = useMemo(() => Object.values(columnMap).filter(Boolean) as (keyof CsvRow)[], [columnMap]);
  const missingRequired = requiredFields.filter(f => !mappedFields.includes(f));

  const proceedToConflicts = useCallback(() => {
    const rows: ParsedImportRow[] = dataRows.map((row, ri) => {
      const raw: CsvRow = {} as CsvRow;
      Object.entries(columnMap).forEach(([colIdx, field]) => {
        if (field) (raw as any)[field] = row[parseInt(colIdx)] || "";
      });
      // Fill defaults
      CSV_HEADERS.forEach(h => { if (!raw[h]) (raw as any)[h] = ""; });

      const errors: string[] = [];
      if (!raw.category.trim()) errors.push("Missing category");
      if (!raw.productName.trim()) errors.push("Missing product name");

      // Conflict detection
      let conflict: ParsedImportRow["conflict"] = { type: null };
      if (raw.sku.trim()) {
        for (const p of products) {
          for (const v of p.variants) {
            if (v.sku && v.sku.toLowerCase() === raw.sku.trim().toLowerCase()) {
              conflict = { type: "sku", matchedProductId: p.id, matchedVariantId: v.id, matchedLabel: `${p.displayName} → ${v.name} (SKU: ${v.sku})` };
              break;
            }
          }
          if (conflict.type) break;
        }
      }
      if (!conflict.type && raw.productName.trim() && raw.variantName.trim()) {
        for (const p of products) {
          if (p.displayName.toLowerCase() === raw.productName.trim().toLowerCase()) {
            const matchVar = p.variants.find(v => v.name.toLowerCase() === raw.variantName.trim().toLowerCase());
            if (matchVar) {
              conflict = { type: "name", matchedProductId: p.id, matchedVariantId: matchVar.id, matchedLabel: `${p.displayName} → ${matchVar.name}` };
            }
            break;
          }
        }
      }
      if (!conflict.type && raw.productName.trim()) {
        for (const p of products) {
          if (p.displayName.toLowerCase().includes(raw.productName.trim().toLowerCase().slice(0, 5))) {
            conflict = { type: "fuzzy", matchedProductId: p.id, matchedLabel: `Possible match: ${p.displayName}` };
            break;
          }
        }
      }

      return { rowIndex: ri, raw, valid: errors.length === 0, errors, conflict, action: conflict.type ? "skip" : "add_new" };
    });
    setParsedRows(rows);
    setStep(2);
  }, [dataRows, columnMap, products]);

  /* ── Step 4: Commit ── */
  const summary = useMemo(() => {
    const s = { created: 0, overwritten: 0, merged: 0, skipped: 0, invalid: 0 };
    parsedRows.forEach(r => {
      if (!r.valid) { s.invalid++; return; }
      if (r.action === "add_new") s.created++;
      else if (r.action === "overwrite") s.overwritten++;
      else if (r.action === "merge") s.merged++;
      else s.skipped++;
    });
    return s;
  }, [parsedRows]);

  const commitImport = () => {
    const batch: ImportBatch = {
      id: `imp-${Date.now()}`,
      timestamp: new Date().toISOString(),
      uploadedBy: role,
      fileName,
      summary,
      snapshotBefore: JSON.stringify(products),
      committed: true,
    };

    setProducts(prev => {
      let updated = [...prev];
      parsedRows.forEach(row => {
        if (!row.valid || row.action === "skip") return;
        const catSlug = categories.find(c =>
          c.name.toLowerCase() === row.raw.category.trim().toLowerCase() ||
          c.slug.toLowerCase() === row.raw.category.trim().toLowerCase()
        )?.slug || row.raw.category.trim().toLowerCase().replace(/\s+/g, "_");

        if (row.action === "add_new") {
          let product = updated.find(p => p.displayName.toLowerCase() === row.raw.productName.trim().toLowerCase() && p.categorySlug === catSlug);
          if (!product) {
            product = {
              id: `prod-imp-${Date.now()}-${row.rowIndex}`,
              displayName: row.raw.productName.trim(),
              categorySlug: catSlug,
              active: row.raw.active.toLowerCase() !== "false",
              sortOrder: updated.filter(p => p.categorySlug === catSlug).length + 1,
              notes: row.raw.notes,
              pricingImpact: "Included",
              vendor: row.raw.vendor,
              skus: [],
              variants: [],
            };
            updated.push(product);
          }
          if (row.raw.variantName.trim()) {
            const variant: CatalogVariant = {
              id: `var-imp-${Date.now()}-${row.rowIndex}`,
              productId: product.id,
              name: row.raw.variantName.trim(),
              imageUrl: row.raw.displayImageUrl,
              referenceImageUrl: row.raw.referenceImageUrl,
              active: row.raw.active.toLowerCase() !== "false",
              sortOrder: product.variants.length + 1,
              sku: row.raw.sku,
              vendor: row.raw.vendor,
              cost: row.raw.cost ? parseFloat(row.raw.cost) : null,
              basePrice: row.raw.basePrice ? parseFloat(row.raw.basePrice) : null,
              description: row.raw.description,
            };
            updated = updated.map(p => p.id === product!.id ? { ...p, variants: [...p.variants, variant] } : p);
          }
        } else if (row.action === "overwrite" && row.conflict.matchedProductId) {
          updated = updated.map(p => {
            if (p.id !== row.conflict.matchedProductId) return p;
            if (row.conflict.matchedVariantId) {
              return {
                ...p,
                variants: p.variants.map(v => v.id === row.conflict.matchedVariantId ? {
                  ...v,
                  name: row.raw.variantName.trim() || v.name,
                  imageUrl: row.raw.displayImageUrl || v.imageUrl,
                  referenceImageUrl: row.raw.referenceImageUrl || v.referenceImageUrl,
                  sku: row.raw.sku || v.sku,
                  vendor: row.raw.vendor || v.vendor,
                  cost: row.raw.cost ? parseFloat(row.raw.cost) : v.cost,
                  basePrice: row.raw.basePrice ? parseFloat(row.raw.basePrice) : v.basePrice,
                  description: row.raw.description || v.description,
                } : v),
              };
            }
            return { ...p, notes: row.raw.notes || p.notes, vendor: row.raw.vendor || p.vendor };
          });
        } else if (row.action === "merge" && row.conflict.matchedProductId) {
          // Merge = add as new variant to matched product
          if (row.raw.variantName.trim()) {
            const variant: CatalogVariant = {
              id: `var-mrg-${Date.now()}-${row.rowIndex}`,
              productId: row.conflict.matchedProductId,
              name: row.raw.variantName.trim(),
              imageUrl: row.raw.displayImageUrl,
              referenceImageUrl: row.raw.referenceImageUrl,
              active: row.raw.active.toLowerCase() !== "false",
              sortOrder: 999,
              sku: row.raw.sku,
              vendor: row.raw.vendor,
              cost: row.raw.cost ? parseFloat(row.raw.cost) : null,
              basePrice: row.raw.basePrice ? parseFloat(row.raw.basePrice) : null,
              description: row.raw.description,
            };
            updated = updated.map(p => p.id === row.conflict.matchedProductId ? { ...p, variants: [...p.variants, variant] } : p);
          }
        }
      });
      return updated;
    });

    setImportBatches(prev => [batch, ...prev]);
    setStep(4);
  };

  const revertBatch = (batchId: string) => {
    const batch = importBatches.find(b => b.id === batchId);
    if (!batch) return;
    try {
      const snapshot = JSON.parse(batch.snapshotBefore) as CatalogProduct[];
      setProducts(snapshot);
      setImportBatches(prev => prev.map(b => b.id === batchId ? { ...b, committed: false } : b));
    } catch { /* noop */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" /> Catalog Import
          </DialogTitle>
          {/* Step indicator */}
          <div className="flex items-center gap-1 mt-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1">
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
                  i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                }`}>
                  {i < step ? <CheckCircle2 className="h-3 w-3" /> : null}
                  {i + 1}. {s}
                </div>
                {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              </div>
            ))}
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-1 mt-2" />
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6 py-2">
          {/* ── Step 1: Upload ── */}
          {step === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileText className="h-10 w-10 text-primary" />
              </div>
              <p className="text-sm font-semibold">Upload CSV File</p>
              <p className="text-xs text-muted-foreground text-center max-w-md">
                Upload a CSV file with catalog items. Use "Download Template" to get the expected format.
              </p>
              <label className="cursor-pointer">
                <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} />
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                  <Upload className="h-4 w-4" /> Choose File
                </div>
              </label>
              {fileName && <Badge variant="secondary" className="text-xs">{fileName}</Badge>}
            </div>
          )}

          {/* ── Step 2: Map Columns ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Map CSV Columns</p>
                  <p className="text-xs text-muted-foreground">
                    {dataRows.length} rows detected. Map each CSV column to a catalog field.
                  </p>
                </div>
                {missingRequired.length > 0 && (
                  <Badge variant="destructive" className="text-[10px]">
                    <AlertTriangle className="h-3 w-3 mr-1" /> Missing: {missingRequired.join(", ")}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {headerRow.map((h, i) => (
                  <Card key={i}>
                    <CardContent className="p-2 flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] shrink-0 w-28 justify-center truncate">{h}</Badge>
                      <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                      <Select value={columnMap[String(i)] || ""} onValueChange={v => setColumnMap(prev => ({ ...prev, [String(i)]: v as keyof CsvRow }))}>
                        <SelectTrigger className="h-7 text-xs flex-1">
                          <SelectValue placeholder="— skip —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip">— skip —</SelectItem>
                          {CSV_HEADERS.map(f => (
                            <SelectItem key={f} value={f} disabled={mappedFields.includes(f) && columnMap[String(i)] !== f}>
                              {CSV_HEADER_LABELS[f]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">Preview (first 3 rows):</p>
              <div className="overflow-auto border rounded text-[10px]">
                <table className="w-full">
                  <thead><tr className="bg-muted">
                    {headerRow.map((h, i) => <th key={i} className="px-2 py-1 text-left font-semibold">{columnMap[String(i)] ? CSV_HEADER_LABELS[columnMap[String(i)] as keyof CsvRow] : h}</th>)}
                  </tr></thead>
                  <tbody>
                    {dataRows.slice(0, 3).map((row, ri) => (
                      <tr key={ri} className="border-t">
                        {row.map((cell, ci) => <td key={ci} className="px-2 py-1 truncate max-w-[120px]">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Step 3: Review Conflicts ── */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Review Conflicts & Actions</p>
                  <p className="text-xs text-muted-foreground">
                    {parsedRows.filter(r => r.conflict.type).length} conflicts detected.
                    Set an action for each row.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default" className="text-[10px]">{parsedRows.filter(r => r.valid && r.action === "add_new").length} New</Badge>
                  <Badge variant="outline" className="text-[10px]">{parsedRows.filter(r => r.conflict.type).length} Conflicts</Badge>
                  <Badge variant="destructive" className="text-[10px]">{parsedRows.filter(r => !r.valid).length} Invalid</Badge>
                </div>
              </div>
              <div className="space-y-1.5 max-h-[400px] overflow-auto">
                {parsedRows.map((row, i) => (
                  <Card key={i} className={!row.valid ? "border-destructive/30" : row.conflict.type ? "border-warning/30" : ""}>
                    <CardContent className="p-2 flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-6 shrink-0">#{row.rowIndex + 1}</span>
                      {!row.valid ? (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      ) : row.conflict.type ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">
                          {row.raw.productName}{row.raw.variantName ? ` → ${row.raw.variantName}` : ""}
                        </p>
                        {row.conflict.matchedLabel && (
                          <p className="text-[10px] text-warning truncate">
                            {row.conflict.type === "sku" ? "SKU match" : row.conflict.type === "name" ? "Exact match" : "Fuzzy match"}: {row.conflict.matchedLabel}
                          </p>
                        )}
                        {row.errors.length > 0 && <p className="text-[10px] text-destructive">{row.errors.join(", ")}</p>}
                      </div>
                      {row.valid && (
                        <Select value={row.action} onValueChange={v => setParsedRows(prev => prev.map((r, ri) => ri === i ? { ...r, action: v as ConflictAction } : r))}>
                          <SelectTrigger className="h-6 text-[10px] w-36 shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="add_new"><div className="flex items-center gap-1"><Plus className="h-3 w-3" /> Add as New</div></SelectItem>
                            {row.conflict.type && (
                              <>
                                <SelectItem value="overwrite"><div className="flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Overwrite</div></SelectItem>
                                <SelectItem value="merge"><div className="flex items-center gap-1"><Merge className="h-3 w-3" /> Merge</div></SelectItem>
                              </>
                            )}
                            <SelectItem value="skip"><div className="flex items-center gap-1"><SkipForward className="h-3 w-3" /> Skip</div></SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 4: Preview ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold">Import Summary</p>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { label: "Create", count: summary.created, color: "text-success", icon: Plus },
                  { label: "Overwrite", count: summary.overwritten, color: "text-info", icon: RefreshCw },
                  { label: "Merge", count: summary.merged, color: "text-primary", icon: Merge },
                  { label: "Skip", count: summary.skipped, color: "text-muted-foreground", icon: SkipForward },
                  { label: "Invalid", count: summary.invalid, color: "text-destructive", icon: XCircle },
                ].map(s => (
                  <Card key={s.label}>
                    <CardContent className="p-3 text-center">
                      <s.icon className={`h-5 w-5 mx-auto mb-1 ${s.color}`} />
                      <p className={`text-lg font-bold ${s.color}`}>{s.count}</p>
                      <p className="text-[10px] text-muted-foreground">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="border rounded p-3 bg-muted/30">
                <p className="text-xs font-semibold mb-2">Changes to apply:</p>
                <div className="space-y-1 max-h-[250px] overflow-auto text-[11px]">
                  {parsedRows.filter(r => r.valid && r.action !== "skip").map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant={r.action === "add_new" ? "default" : r.action === "overwrite" ? "outline" : "secondary"} className="text-[9px] h-4 w-20 justify-center">
                        {r.action === "add_new" ? "CREATE" : r.action === "overwrite" ? "OVERWRITE" : "MERGE"}
                      </Badge>
                      <span>{r.raw.category} / {r.raw.productName}{r.raw.variantName ? ` → ${r.raw.variantName}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-warning">Confirm Import</p>
                  <p className="text-[10px] text-warning">
                    This will modify the live catalog. A snapshot will be saved for rollback.
                    Existing jobs will not be affected.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 5: Done ── */}
          {step === 4 && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="h-20 w-20 rounded-2xl bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-success" />
              </div>
              <p className="text-sm font-semibold">Import Complete</p>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div><p className="text-lg font-bold text-success">{summary.created}</p><p className="text-[10px] text-muted-foreground">Created</p></div>
                <div><p className="text-lg font-bold text-info">{summary.overwritten}</p><p className="text-[10px] text-muted-foreground">Overwritten</p></div>
                <div><p className="text-lg font-bold text-primary">{summary.merged}</p><p className="text-[10px] text-muted-foreground">Merged</p></div>
                <div><p className="text-lg font-bold text-muted-foreground">{summary.skipped}</p><p className="text-[10px] text-muted-foreground">Skipped</p></div>
              </div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div>
            {step > 0 && step < 4 && (
              <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => setStep(s => s - 1)}>
                <ArrowLeft className="h-3 w-3" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { reset(); onOpenChange(false); }}>
              {step === 4 ? "Close" : "Cancel"}
            </Button>
            {step === 1 && (
              <Button size="sm" className="text-xs gap-1" disabled={missingRequired.length > 0} onClick={proceedToConflicts}>
                Validate & Continue <ChevronRight className="h-3 w-3" />
              </Button>
            )}
            {step === 2 && (
              <Button size="sm" className="text-xs gap-1" onClick={() => setStep(3)}>
                Preview Import <ChevronRight className="h-3 w-3" />
              </Button>
            )}
            {step === 3 && (
              <Button size="sm" className="text-xs gap-1 bg-success hover:bg-success" onClick={commitImport}>
                <CheckCircle2 className="h-3 w-3" /> Commit Import
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Import History Dialog ── */
export function ImportHistoryDialog({ open, onOpenChange, batches, onRevert }: {
  open: boolean; onOpenChange: (o: boolean) => void;
  batches: ImportBatch[]; onRevert: (id: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" /> Import History
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[400px]">
          {batches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No imports yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {batches.map(b => (
                <Card key={b.id}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-semibold">{b.fileName}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(b.timestamp).toLocaleString()} • by {b.uploadedBy}
                        </p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="default" className="text-[9px] h-4">{b.summary.created} created</Badge>
                          <Badge variant="outline" className="text-[9px] h-4">{b.summary.overwritten} overwritten</Badge>
                          <Badge variant="secondary" className="text-[9px] h-4">{b.summary.merged} merged</Badge>
                        </div>
                      </div>
                      {b.committed && (
                        <Button variant="outline" size="sm" className="text-[10px] h-6 text-destructive" onClick={() => onRevert(b.id)}>
                          Revert
                        </Button>
                      )}
                      {!b.committed && <Badge variant="secondary" className="text-[9px]">Reverted</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── CSV Parser ── */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

/* ── CSV Template Generator ── */
export function downloadCsvTemplate() {
  const headers = CSV_HEADERS.map(h => CSV_HEADER_LABELS[h]).join(",");
  const example = [
    "Wall Systems", "Sentrel", "Arctic White", "https://example.com/img.jpg",
    "https://example.com/ref.jpg", "WALL-SEN-AW", "Sentrel Inc", "499.99",
    "250.00", "true", "Durable PVC panel", "Standard size"
  ].join(",");
  const csv = `${headers}\n${example}\n`;
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "catalog_import_template.csv"; a.click();
  URL.revokeObjectURL(url);
}

/* ── Export Current Catalog as CSV ── */
export function exportCurrentCatalog(products: CatalogProduct[], categories: { name: string; slug: string }[]) {
  const headers = CSV_HEADERS.map(h => CSV_HEADER_LABELS[h]).join(",");

  const rows: string[] = [];

  products.forEach(product => {
    const category = categories.find(c => c.slug === product.categorySlug);
    const categoryName = category?.name || product.categorySlug;

    if (product.variants && product.variants.length > 0) {
      product.variants.forEach(variant => {
        const row = [
          categoryName,
          product.displayName,
          variant.name,
          variant.imageUrl || "",
          variant.referenceImageUrl || "",
          variant.sku || "",
          variant.vendor || product.vendor || "",
          variant.basePrice?.toString() || "",
          variant.cost?.toString() || "",
          variant.active ? "true" : "false",
          variant.description || product.notes || "",
          "",
        ];
        rows.push(row.map(field => {
          const str = String(field);
          if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(","));
      });
    } else {
      const row = [
        categoryName,
        product.displayName,
        "",
        "",
        "",
        "",
        product.vendor || "",
        "",
        "",
        product.active ? "true" : "false",
        product.notes || "",
        "",
      ];
      rows.push(row.map(field => {
        const str = String(field);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(","));
    }
  });

  const csv = `${headers}\n${rows.join("\n")}\n`;
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `catalog_export_${new Date().toISOString().split("T")[0]}.csv`; a.click();
  URL.revokeObjectURL(url);
}
